import { memo, useCallback, useState } from 'react';

import {
  ColorDropdownPicker,
  type ColorDropdownPickerCopy,
  type ColorDropdownPickerValue,
} from '@air/component-color-dropdown-picker';

import { WorkspaceAgentGuidedVariantColorOptionField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantColorOptionField';
import { WorkspaceAgentGuidedVariantRemoveBackgroundPreview } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantRemoveBackgroundPreview';
import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import { type WorkspaceAgentGuidedVariantBrandKit } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantBrandKit';
import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  getWorkspaceAgentGuidedVariantColorOptionLabel,
  WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import { type WorkspaceAgentGuidedVariantImagePreviewSelection } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePreviewSelection';
import { buildWorkspaceAgentRemoveBackgroundPrompt } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRemoveBackground';

const REMOVE_BACKGROUND_COLOR_PICKER_COPY: ColorDropdownPickerCopy = {
  customColor: 'Custom color',
  customColorInputLabel: 'Custom background color',
  groupsTitle: 'Context colors',
};

export type WorkspaceAgentGuidedVariantRemoveBackgroundSettingsProps = {
  brandKits: readonly WorkspaceAgentGuidedVariantBrandKit[];
  isBrandKitLoading: boolean;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
  selection: WorkspaceAgentGuidedVariantImagePreviewSelection;
};

export const WorkspaceAgentGuidedVariantRemoveBackgroundSettings = memo(
  ({
    brandKits,
    isBrandKitLoading,
    onGenerate,
    selection,
  }: WorkspaceAgentGuidedVariantRemoveBackgroundSettingsProps) => {
    const [backgroundId, setBackgroundId] = useState<WorkspaceAgentGuidedVariantColorOptionId>('transparent');
    const [customColor, setCustomColor] = useState<ColorDropdownPickerValue | null>(null);
    const firstBrandColor = brandKits.flatMap((kit) => kit.colors)[0] ?? null;
    const effectiveCustomColor =
      customColor?.hex ?? firstBrandColor?.hex ?? WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR;
    const effectiveCustomLabel = customColor ? customColor.label : (firstBrandColor?.label ?? null);
    const isGenerateDisabled = selection.imageCount < 1;
    const generateLabel = `Generate ${selection.imageCount.toLocaleString()}`;
    const selectedColorLabel = getWorkspaceAgentGuidedVariantColorOptionLabel(backgroundId);
    const previewImageAlt = `${selectedColorLabel} background preview`;

    const handleBackgroundChange = useCallback((nextBackgroundId: WorkspaceAgentGuidedVariantColorOptionId) => {
      setBackgroundId(nextBackgroundId);
    }, []);

    const handleGenerate = useCallback(() => {
      if (selection.imageCount < 1) {
        return;
      }

      const color = getWorkspaceAgentGuidedVariantCanvasColor(backgroundId, effectiveCustomColor);
      let description = `${selectedColorLabel} background`;

      if (backgroundId === 'custom' && color) {
        description = effectiveCustomLabel ? `${effectiveCustomLabel} ${color}` : `Custom background ${color}`;
      }

      onGenerate({
        allowsJpeg: backgroundId !== 'transparent',
        kind: 'content',
        build: (destination, output) =>
          buildWorkspaceAgentRemoveBackgroundPrompt({
            backgroundId,
            customColor: effectiveCustomColor,
            destination,
            imageCount: selection.imageCount,
            output,
            skippedFileCount: selection.skippedFileCount,
            skippedVideoCount: selection.skippedVideoCount,
          }),
        structuredDisplay: {
          description,
          icon: 'eraser',
          title: 'Remove background',
        },
      });
    }, [backgroundId, effectiveCustomColor, effectiveCustomLabel, onGenerate, selectedColorLabel, selection]);

    return (
      <div className="flex flex-col gap-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_REMOVE_BACKGROUND_SETTINGS">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-2">
            <WorkspaceAgentGuidedVariantColorOptionField
              legend="New background"
              onValueChange={handleBackgroundChange}
              options={WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS}
              value={backgroundId}
            />
            {backgroundId === 'custom' ? (
              <ColorDropdownPicker
                colorGroups={brandKits}
                copy={REMOVE_BACKGROUND_COLOR_PICKER_COPY}
                hex={effectiveCustomColor}
                isLoading={isBrandKitLoading}
                label={effectiveCustomLabel}
                onChange={setCustomColor}
              />
            ) : null}
          </div>
          <WorkspaceAgentGuidedVariantRemoveBackgroundPreview
            backgroundId={backgroundId}
            customColor={effectiveCustomColor}
            imageAlt={previewImageAlt}
            previewHeight={selection.previewHeight}
            previewImageUrl={selection.previewImageUrl}
            previewWidth={selection.previewWidth}
          />
        </div>
        <WorkspaceAgentGuidedVariantSettingsActions
          generateLabel={generateLabel}
          isGenerateDisabled={isGenerateDisabled}
          onGenerate={handleGenerate}
        />
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantRemoveBackgroundSettings.displayName = 'WorkspaceAgentGuidedVariantRemoveBackgroundSettings';
